// Basketball workspace — section router. Receives a `section` string from the
// route table (routes.tsx passes section="overview" | "slate" | … |
// "big6-<slug>") and dispatches to the right ported screen. Every screen reads
// its real hook and renders its honest-empty / dormant state when there's no
// data — dormant IS the production surface today (the basketball layer is in
// calibration; sportsRegistry keeps it 'planned', the coverage/calibration/CLV
// gates govern activation). It never renders a model price, edge, or pick it has
// not earned.

import { type BasketballSection } from "./_bbkit.tsx";
import { isBig6Persona } from "./big6Registry.ts";
import { OverviewScreen } from "./OverviewScreen.tsx";
import { SlateScreen } from "./SlateScreen.tsx";
import { SignalsScreen } from "./SignalsScreen.tsx";
import { BballSinglesScreen } from "./BballSinglesScreen.tsx";
import { ProductScreen } from "./BasketballProducts.tsx";
import { GauntletScreen } from "./GauntletScreen.tsx";
import { Big6PersonaScreen } from "./Big6PersonaScreen.tsx";
import { ClvScreen, NoBetsScreen, LeaguesScreen } from "./Analysis.tsx";

export type { BasketballSection };

export function BasketballPage({ section = "overview" }: { section?: BasketballSection }): JSX.Element {
  // THE BIG 6 persona screens (big6-<slug>).
  if (section.startsWith("big6-")) {
    const slug = section.slice(5);
    if (isBig6Persona(slug)) return <Big6PersonaScreen persona={slug} />;
  }

  switch (section) {
    case "slate": return <SlateScreen />;
    case "signals": return <SignalsScreen />;
    case "singles": return <BballSinglesScreen />;
    case "gauntlet": return <GauntletScreen />;
    case "clv": return <ClvScreen />;
    case "no-bets": return <NoBetsScreen />;
    case "leagues": return <LeaguesScreen />;
    default: break;
  }

  // Acca product screens (read-backed; honest-empty).
  const product = ProductScreen({ section });
  if (product) return product;

  // Overview (default).
  return <OverviewScreen />;
}
